import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  KnowYourCustomerData,
  KnowYourCustomerDataResponse,
  KnowYourCustomerSegmentRow
} from '@blisspointmedia/bpm-types/dist/KnowYourCustomer';
import { awaitJSON, MiscLambdaFetch } from '../../utils/fetch-utils';

interface KnowYourCustomerSegmentDataResult {
  rows: KnowYourCustomerData[];
  lastUpdated: Date;
}

export const useKnowYourCustomerSegments = (
  cid: string
): ReturnType<typeof useQuery<KnowYourCustomerSegmentRow[]>> =>
  useQuery({
    meta: { suppressGlobalError: true },
    queryKey: ['knowYourCustomerSegments', cid],
    queryFn: async () => {
      const response = await MiscLambdaFetch('/list_know_your_customer_configs', {
        method: 'GET',
        params: { company: cid }
      });
      const segments = await awaitJSON<KnowYourCustomerSegmentRow[]>(response);
      return [...segments].sort((a, b) => a.ordering - b.ordering);
    },
    enabled: Boolean(cid)
  });

export const useKnowYourCustomerSegmentData = (
  segmentId: number | undefined
): ReturnType<typeof useQuery<KnowYourCustomerSegmentDataResult>> =>
  useQuery({
    meta: { suppressGlobalError: true },
    queryKey: ['knowYourCustomerSegmentData', segmentId],
    queryFn: async () => {
      const response = await MiscLambdaFetch<{ id: string }>('/get_know_your_customer_config', {
        method: 'GET',
        params: { id: String(segmentId) }
      });
      const { rows, lastUpdated }: KnowYourCustomerDataResponse = await awaitJSON(response);

      const typedRows: KnowYourCustomerData[] = rows.map(row => ({
        clientPopulation: Number(row.client_population),
        clientPopulationFraction: Number(row.client_population_fraction),
        fieldName: row.field_name,
        levels: row.levels,
        tuPopulation: Number(row.tu_population),
        tuPopulationFraction: Number(row.tu_population_fraction)
      }));

      return {
        rows: typedRows,
        lastUpdated: new Date(lastUpdated)
      };
    },
    enabled: segmentId !== undefined,
    // Keep the previous segment's data in place while the next segment loads
    // so the page header controls (segment dropdown, sort, toggles) stay
    // rendered instead of the whole page dropping to the full-page spinner.
    placeholderData: keepPreviousData
  });
